import { Ability, AbilityBuilder } from '@casl/ability';
import { NotFoundError } from '../types/errors';
import { collapseAbilityRules } from './collapseAbilityRules';
import applyOrganizationMemberAbilities from './organizationMemberAbility';
import { normalizeScopeName } from './parseScopes';
import { projectMemberAbilities } from './projectMemberAbility';
import { buildAbilityFromScopes } from './scopeAbilityBuilder';
export const JWT_HEADER_NAME = 'lightdash-embed-token';
export const getUserAbilityBuilder = ({ user, orgExtraRoleUuids = [], projectProfiles, permissionsConfig, customRoleScopes, customRolesEnabled, isEnterprise, patScopeAuthoritative, }) => {
    const builder = new AbilityBuilder(Ability);
    const invalidScopes = [];
    // Authoritative only when the org opted in AND the deployment is
    // licensed — unlicensed orgs keep inheriting the deployment default.
    const patScopeIsAuthoritative = Boolean(isEnterprise && patScopeAuthoritative);
    const applyPatConfigFallback = !patScopeIsAuthoritative;
    // Only the org primary slot may speak for the scope while authoritative;
    // downstream sets listing it would silently restore what it withheld.
    const withoutDownstreamPatScope = (scopes) => patScopeIsAuthoritative
        ? scopes.filter((scope) => normalizeScopeName(scope) !==
            'manage:PersonalAccessToken')
        : scopes;
    // Extra custom roles are unioned on top of the slot; unknown uuids are
    // skipped (logged) rather than granting anything.
    const applyExtraRoles = (extraRoleUuids, apply) => {
        if (!customRolesEnabled) {
            return;
        }
        extraRoleUuids.forEach((roleUuid) => {
            const scopes = customRoleScopes?.[roleUuid];
            if (!scopes) {
                // eslint-disable-next-line no-console
                console.error(`Custom role with uuid ${roleUuid} was not found`);
                return;
            }
            invalidScopes.push(...apply(scopes));
        });
    };
    if (user.role && user.organizationUuid) {
        // Org-level custom role: if the user's organization_memberships row
        // points at a role_uuid AND custom roles are enabled AND we have the
        // role's scopes, build CASL from those scopes (same path as
        // project-level custom roles below). Falls back to the system role
        // path otherwise.
        const orgCustomRoleScopes = customRolesEnabled && user.roleUuid
            ? customRoleScopes?.[user.roleUuid]
            : undefined;
        if (orgCustomRoleScopes) {
            invalidScopes.push(...buildAbilityFromScopes({
                organizationUuid: user.organizationUuid,
                userUuid: user.userUuid,
                scopes: orgCustomRoleScopes,
                isEnterprise,
                organizationRole: user.role,
                permissionsConfig,
                // While the org's PAT opt-in is authoritative, the primary slot alone
                // decides token access; downstream sets are stripped and declined.
                applyPatConfigFallback,
            }, builder));
        }
        else {
            applyOrganizationMemberAbilities({
                role: user.role,
                member: {
                    organizationUuid: user.organizationUuid,
                    userUuid: user.userUuid,
                },
                builder,
                permissionsConfig,
            });
        }
        applyExtraRoles(orgExtraRoleUuids, (scopes) => buildAbilityFromScopes({
            organizationUuid: user.organizationUuid,
            userUuid: user.userUuid,
            scopes: withoutDownstreamPatScope(scopes),
            isEnterprise,
            organizationRole: user.role,
            permissionsConfig,
            applyPatConfigFallback,
        }, builder));
        projectProfiles.forEach((projectProfile) => {
            if (projectProfile.roleUuid && customRolesEnabled) {
                if (!user.organizationUuid) {
                    throw new NotFoundError(`Organization with uuid ${user.organizationUuid} was not found`);
                }
                const scopes = customRoleScopes?.[projectProfile.roleUuid];
                if (!scopes) {
                    // eslint-disable-next-line no-console
                    console.error(`Custom role with uuid ${projectProfile.roleUuid} was not found`);
                    return;
                }
                invalidScopes.push(...buildAbilityFromScopes({
                    projectUuid: projectProfile.projectUuid,
                    projectType: projectProfile.projectType,
                    projectCreatedByUserUuid: projectProfile.projectCreatedByUserUuid,
                    userUuid: user.userUuid,
                    scopes: withoutDownstreamPatScope(scopes),
                    isEnterprise,
                    organizationRole: user.role,
                    permissionsConfig,
                    applyPatConfigFallback,
                }, builder));
            }
            else {
                projectMemberAbilities[projectProfile.role](projectProfile, builder);
            }
            applyExtraRoles(projectProfile.extraRoleUuids ?? [], (scopes) => buildAbilityFromScopes({
                projectUuid: projectProfile.projectUuid,
                projectType: projectProfile.projectType,
                projectCreatedByUserUuid: projectProfile.projectCreatedByUserUuid,
                userUuid: user.userUuid,
                scopes: withoutDownstreamPatScope(scopes),
                isEnterprise,
                organizationRole: user.role,
                permissionsConfig,
                applyPatConfigFallback,
            }, builder));
        });
    }
    // Collapse per-project rules into `{ $in: [...] }` so the rule set (and the
    // serialized `abilityRules` payload) scales with role tiers, not project count.
    builder.rules = collapseAbilityRules(builder.rules);
    return { builder, invalidScopes };
};
// Defines user ability for test purposes
export const defineUserAbility = (user, projectProfiles, customRoleScopes) => {
    const { builder } = getUserAbilityBuilder({
        user,
        projectProfiles,
        permissionsConfig: {
            pat: {
                enabled: false,
                allowedOrgRoles: [],
            },
        },
        customRoleScopes,
    });
    return builder.build();
};
//# sourceMappingURL=index.js.map